/**
 * <hugh-collection>: progressive enhancement for sections/hugh-collection.liquid.
 * Without JS the filters are a GET form and every link is a normal link.
 * With JS: a filter, sort or page change re-renders the section through the Section Rendering
 * API and updates the address (back and forward work); on narrow screens the filter panel
 * opens as a full-screen layer.
 */
class HughCollection extends HTMLElement {
  #abort = new AbortController();
  /** @type {AbortController | undefined} */
  #request;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    const signal = this.#abort.signal;
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    window.addEventListener('popstate', () => this.#render(window.location.href, false), { signal });
  }

  /** Phones: the arrow beside the title opens and closes the collection description. @param {HTMLElement} toggle */
  #toggleIntro(toggle) {
    const intro = this.querySelector('[data-intro]');
    if (!intro) return;
    const open = !intro.hasAttribute('data-open');
    intro.toggleAttribute('data-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
    // An open panel locks the page's scroll; release it when the section goes (the theme editor
    // re-renders a section on every change), or the page stays frozen.
    if (this.querySelector('[data-filters][data-open]')) document.documentElement.style.overflow = '';
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return this.querySelector('[data-filter-form]');
  }

  /** Builds the collection URL from the filter form (including the sort select linked by form=). */
  #formUrl() {
    const form = this.form;
    if (!form) return window.location.href;
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
    }
    const url = new URL(form.action, window.location.origin);
    url.search = params.toString();
    return url.toString();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.hasAttribute('data-auto') || target.type === 'number') return;
    this.#render(this.#formUrl());
  };

  /** Price inputs: wait until typing pauses. @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'number') return;
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      if (target.validity.valid) this.#render(this.#formUrl());
    }, 600);
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (event.target !== this.form) return;
    event.preventDefault();
    this.#closePanel();
    this.#render(this.#formUrl());
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[data-filter-link]');
    if (link instanceof HTMLAnchorElement && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      // A height tile is tapped at the top of the page, where its results already show: no jump.
      this.#render(link.href, true, !link.closest('.hugh-heights'));
      return;
    }
    const intro = target?.closest('[data-intro-toggle]');
    if (intro instanceof HTMLElement) this.#toggleIntro(intro);
    if (target?.closest('[data-filters-open]')) this.#openPanel();
    if (target?.closest('[data-filters-close]')) this.#closePanel();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.querySelector('[data-filters][data-open]')) this.#closePanel();
  };

  #openPanel() {
    const panel = this.querySelector('[data-filters]');
    if (!panel) return;
    panel.setAttribute('data-open', '');
    this.querySelector('[data-filters-open]')?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    panel.querySelector('summary, input, a')?.focus();
  }

  #closePanel() {
    const panel = this.querySelector('[data-filters]');
    if (!panel?.hasAttribute('data-open')) return;
    panel.removeAttribute('data-open');
    document.documentElement.style.overflow = '';
    const opener = this.querySelector('[data-filters-open]');
    opener?.setAttribute('aria-expanded', 'false');
    if (opener instanceof HTMLElement) opener.focus();
  }

  /**
   * Fetches the section for a URL and swaps it in. Newer requests cancel older ones.
   * @param {string} href
   * @param {boolean} [push]
   * @param {boolean} [jump] - Move the toolbar to the top of the screen afterwards
   */
  async #render(href, push = true, jump = push) {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    this.#request?.abort();
    this.#request = new AbortController();
    const results = this.querySelector('[data-results]');
    results?.setAttribute('aria-busy', 'true');

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.dataset.section || '');
    try {
      const response = await fetch(fetchUrl, { signal: this.#request.signal });
      if (!response.ok) throw new Error(`Collection ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('hugh-collection');
      if (!next) throw new Error('Collection markup missing');
      // Read the panel state now, not when the request started: "Show results" closes the panel while a
      // filter's request is still on its way, and the panel must stay closed when the results arrive.
      const panelOpen = this.querySelector('[data-filters][data-open]') !== null;
      this.replaceChildren(...[...next.childNodes].map((node) => document.importNode(node, true)));
      if (panelOpen) this.querySelector('[data-filters]')?.setAttribute('data-open', '');
      if (push) history.pushState({}, '', url);
      // After the shopper's own change the toolbar moves to the top of the screen, so the results start
      // right under it (owner, 24 Oct 2026); otherwise it only comes back when it is above the screen.
      if (!panelOpen) {
        const top = this.querySelector('.hugh-collection__bar');
        const y = top?.getBoundingClientRect().top ?? 0;
        if (top && (jump ? Math.abs(y) > 1 : y < 0)) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          top.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        }
      }
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      window.location.assign(url);
    }
  }
}

if (!customElements.get('hugh-collection')) customElements.define('hugh-collection', HughCollection);
